import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  useNavigate,
  useSearch
} from '@tanstack/react-router';
import {
  ArrowLeft,
  FileText,
  Image as ImageIcon,
  Loader2,
  Maximize2
} from 'lucide-react';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import { ApiError, api } from '~/lib/api';
import { requireSession } from '~/lib/route-auth';
import { cn } from '~/lib/utils';

const PREVIEW_QUERY_OPTIONS = {
  staleTime: Number.POSITIVE_INFINITY,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false
} as const;

function isSvgMimeType(mimeType: string): boolean {
  return mimeType === 'image/svg+xml' || mimeType === 'image/svg';
}

function isRasterImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith('image/') && !isSvgMimeType(mimeType);
}

async function blobStartsWithPdfHeader(blob: Blob): Promise<boolean> {
  const bytes = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  const pdfHeader = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
  return pdfHeader.every((byte, index) => bytes[index] === byte);
}

export const Route = createFileRoute('/rooms/$roomId/files/$fileId')({
  beforeLoad: async () => {
    await requireSession();
  },
  component: FilePreviewRoute
});

async function loadPreviewBlob(
  url: string,
  expectedMimeType: string
): Promise<Blob> {
  const res = await api.raw(url, { cache: 'no-store' });
  if (!res.ok) {
    const body = await res.text();
    let msg = res.statusText || 'Failed to load file';
    try {
      const j = JSON.parse(body.trim()) as {
        error?: unknown;
        message?: unknown;
      };
      if (typeof j.error === 'string' && j.error) msg = j.error;
      else if (typeof j.message === 'string' && j.message) msg = j.message;
    } catch {
      const t = body.trim();
      if (t && t.length < 300 && !t.startsWith('<')) msg = t;
    }
    throw new ApiError(msg, res.status);
  }

  const contentType = res.headers.get('Content-Type') ?? '';
  if (contentType.startsWith('text/html')) {
    throw new ApiError(
      'Preview returned the app shell instead of file content.',
      res.status
    );
  }

  const blob = await res.blob();
  if (
    expectedMimeType.startsWith('application/pdf') &&
    !(await blobStartsWithPdfHeader(blob))
  ) {
    throw new ApiError(
      'Preview content did not match the expected PDF format.',
      res.status
    );
  }

  return blob;
}

function FilePreviewRoute() {
  const { roomId, fileId } = Route.useParams();
  const navigate = useNavigate();
  const previewShellRef = React.useRef<HTMLElement>(null);
  const search = useSearch({ from: '/rooms/$roomId/files/$fileId' }) as {
    folderId?: string;
  };

  const previewUrl = `/api/files/${fileId}/download?disposition=inline`;

  const metaQuery = useQuery({
    queryKey: ['files', fileId, 'preview-meta'],
    queryFn: () =>
      api.get<{
        file: { id: string; name: string; mimeType: string; sizeBytes: number };
        contentReady: boolean;
      }>(`/api/files/${fileId}/meta`),
    ...PREVIEW_QUERY_OPTIONS
  });

  const fileName = metaQuery.data?.file.name ?? 'File preview';

  const mimeType = metaQuery.data?.file.mimeType ?? '';
  const contentReady = metaQuery.data?.contentReady ?? false;
  const isPdf = mimeType.startsWith('application/pdf');
  const isImage = isRasterImageMimeType(mimeType);
  const unsupported = !!mimeType && !isPdf && !isImage;

  const previewBlobQuery = useQuery({
    queryKey: ['files', fileId, 'preview-blob'],
    enabled: metaQuery.isSuccess && contentReady && (isPdf || isImage),
    queryFn: () => loadPreviewBlob(previewUrl, mimeType),
    ...PREVIEW_QUERY_OPTIONS
  });

  const [previewObjectUrl, setPreviewObjectUrl] = React.useState<string | null>(
    null
  );

  React.useLayoutEffect(() => {
    const blob = previewBlobQuery.data;
    if (!blob) {
      setPreviewObjectUrl(null);
      return;
    }
    const u = URL.createObjectURL(blob);
    setPreviewObjectUrl(u);
    return () => {
      URL.revokeObjectURL(u);
    };
  }, [previewBlobQuery.data]);

  const canShowPreview =
    !!previewObjectUrl && contentReady && (isPdf || isImage);

  const openPreviewFullscreen = React.useCallback(() => {
    const previewShell = previewShellRef.current;
    if (!previewShell || typeof previewShell.requestFullscreen !== 'function') {
      return;
    }
    void previewShell.requestFullscreen().catch(() => {
      /* Fullscreen can be blocked by browser policy; leave the preview as-is. */
    });
  }, []);

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b bg-card px-4 py-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() =>
            navigate({
              to: '/rooms/$roomId',
              params: { roomId },
              search: { folderId: search.folderId }
            })
          }
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back
        </Button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isImage ? (
              <ImageIcon className="h-4 w-4 text-sky-400/90" aria-hidden />
            ) : (
              <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
            )}
            <p className="truncate text-sm font-medium">{fileName}</p>
          </div>
          {mimeType && (
            <p className="truncate text-xs text-muted-foreground">{mimeType}</p>
          )}
        </div>
        {canShowPreview ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={openPreviewFullscreen}
          >
            <Maximize2 className="h-4 w-4" aria-hidden />
            Full screen
          </Button>
        ) : null}
      </header>

      <main ref={previewShellRef} className="min-h-0 flex-1 bg-muted/20">
        {metaQuery.isLoading ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading preview…
          </div>
        ) : metaQuery.isError ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div className="max-w-lg space-y-2">
              <p className="text-sm font-medium text-foreground">
                Couldn’t load preview
              </p>
              <p className="text-sm text-muted-foreground">
                {metaQuery.error instanceof Error
                  ? metaQuery.error.message
                  : 'Unknown error'}
              </p>
              <div className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => metaQuery.refetch()}
                >
                  Try again
                </Button>
              </div>
            </div>
          </div>
        ) : !contentReady ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div className="max-w-lg space-y-2">
              <p className="text-sm font-medium text-foreground">
                File isn’t available yet
              </p>
              <p className="text-sm text-muted-foreground">
                The file record exists, but the content wasn’t found in storage.
                Try uploading again, or confirm the API is using the same R2
                bucket that received the upload.
              </p>
              <div className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      `/api/files/${fileId}/download?disposition=attachment`,
                      '_blank'
                    )
                  }
                >
                  Try download
                </Button>
              </div>
            </div>
          </div>
        ) : !previewUrl ? (
          <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
            No preview available.
          </div>
        ) : unsupported ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div className="max-w-lg space-y-2">
              <p className="text-sm font-medium text-foreground">
                Preview not supported
              </p>
              <p className="text-sm text-muted-foreground">
                This file type can’t be previewed yet. You can still download
                it.
              </p>
              <div className="pt-2">
                <Button
                  type="button"
                  onClick={() =>
                    window.open(
                      `/api/files/${fileId}/download?disposition=attachment`,
                      '_blank'
                    )
                  }
                >
                  Download
                </Button>
              </div>
            </div>
          </div>
        ) : isPdf ? (
          previewBlobQuery.isPending ? (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading PDF…
            </div>
          ) : previewBlobQuery.isError ? (
            <div className="flex h-full items-center justify-center p-6 text-center">
              <div className="max-w-lg space-y-2">
                <p className="text-sm font-medium text-foreground">
                  Couldn’t load PDF
                </p>
                <p className="text-sm text-muted-foreground">
                  {previewBlobQuery.error instanceof Error
                    ? previewBlobQuery.error.message
                    : 'Unknown error'}
                </p>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void previewBlobQuery.refetch()}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            </div>
          ) : previewObjectUrl ? (
            <iframe
              title={fileName}
              src={previewObjectUrl}
              className="h-full w-full"
            />
          ) : null
        ) : isImage ? (
          <div className="flex h-full w-full flex-col items-center justify-center p-4">
            {previewBlobQuery.isPending ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading image…
              </div>
            ) : previewBlobQuery.isError ? (
              <div className="max-w-lg space-y-2 text-center">
                <p className="text-sm font-medium text-foreground">
                  Couldn’t load image
                </p>
                <p className="text-sm text-muted-foreground">
                  {previewBlobQuery.error instanceof Error
                    ? previewBlobQuery.error.message
                    : 'Unknown error'}
                </p>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void previewBlobQuery.refetch()}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            ) : previewObjectUrl ? (
              <img
                src={previewObjectUrl}
                alt={fileName}
                className={cn(
                  'max-h-full max-w-full select-none object-contain'
                )}
              />
            ) : null}
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center p-6 text-sm text-muted-foreground">
            No preview available.
          </div>
        )}
      </main>
    </div>
  );
}
